import { useRef, useEffect, useCallback } from 'react';
import { useSwipeable } from 'react-swipeable';

/**
 * Custom hook for handling swipe gestures and keyboard navigation
 * Provides TikTok-style vertical swipe navigation and desktop keyboard controls
 * Now using react-swipeable for better swipe detection
 */
const useShortsSwipe = ({ 
  onSwipeUp, 
  onSwipeDown, 
  onSpacePress, 
  onMutePress 
}) => {
  const containerRef = useRef(null);

  // Configure swipeable handlers with proper settings for vertical swipes
  const swipeHandlers = useSwipeable({
    onSwipedUp: (eventData) => {
      console.log('🔼 Swiped Up!', {
        deltaY: eventData.deltaY,
        velocity: eventData.velocity,
        dir: eventData.dir
      });
      // Swipe up = next video (like TikTok)
      onSwipeUp?.();
    },
    onSwipedDown: (eventData) => {
      console.log('🔽 Swiped Down!', {
        deltaY: eventData.deltaY,
        velocity: eventData.velocity,
        dir: eventData.dir
      });
      // Swipe down = previous video
      onSwipeDown?.();
    },
    onSwiping: (eventData) => {
      console.log('↕️ Swiping...', {
        deltaY: eventData.deltaY,
        dir: eventData.dir
      });
      // Prevent default scrolling while swiping
      if (Math.abs(eventData.deltaY) > 5) {
        eventData.event.preventDefault();
      }
    },
    onTouchStartOrOnMouseDown: (eventData) => {
      console.log('👆 Touch/Mouse Start', eventData);
    },
    // Configuration options
    delta: 10, // Lower threshold for easier swiping
    preventScrollOnSwipe: true, // Prevent scroll during swipe
    trackTouch: true, // Track touch input
    trackMouse: true, // Also track mouse for testing
    rotationAngle: 0, // Don't rotate touch axis
    swipeDuration: 250, // Max time for swipe
    preventDefaultTouchmoveEvent: true, // Prevent default on touchmove
    touchEventOptions: { passive: false }, // Allow preventDefault
  });

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    // Only handle keyboard events when shorts viewer is active
    if (!containerRef.current) return;
    
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        onSwipeDown?.(); // Up arrow = previous video
        break;
      case 'ArrowDown':
        e.preventDefault();
        onSwipeUp?.(); // Down arrow = next video
        break;
      case ' ':
      case 'Spacebar':
        e.preventDefault();
        onSpacePress?.(); // Space = pause/play
        break;
      case 'm':
      case 'M':
        e.preventDefault();
        onMutePress?.(); // M = mute/unmute
        break;
      default:
        break;
    }
  }, [onSwipeUp, onSwipeDown, onSpacePress, onMutePress]);

  // Mouse wheel support for desktop
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    
    if (e.deltaY > 0) {
      // Scrolling down - next video
      onSwipeUp?.();
    } else {
      // Scrolling up - previous video
      onSwipeDown?.();
    }
  }, [onSwipeUp, onSwipeDown]);

  // Set up event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    // Mouse wheel for desktop
    container.addEventListener('wheel', handleWheel, { passive: false });
    
    // Keyboard events
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, handleWheel]);

  // Combine swipe handlers with ref
  return {
    containerRef,
    swipeHandlers
  };
};

export default useShortsSwipe;